// src/pages/BookCover.jsx
import React, {  useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE } from '../utils/api'; 
import CollaboratorBar from '../components/BookCover/CollaboratorBar';
import NewVersionDialog from '../components/BookCover/NewVersionDialog';
import CreateFromExistingDialog from '../components/BookCover/CreateFromExistingDialog';
import VersionSelector from '../components/BookCover/VersionSelector';
import CoverTemplatesModal from '../components/BookCover/CoverTemplatesModal';
import ViewDetailsDialog from '../components/BookCover/ViewDetailsDialog';
import '../styles/BookCover.css';

// Cover status constants - must match backend CoverStatus class
const CoverStatus = {
  IN_PROGRESS: 'in_progress',
  WAITING_FOR_APPROVAL: 'waiting_for_approval',
  APPROVED: 'approved',
  IN_REVIEW: 'in_review',
  REJECTED: 'rejected',
  REVIEWER_APPROVED: 'reviewer_approved',
  ASSIGNED: 'assigned',
};

export default function BookCover() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [hasDesign, setHasDesign] = useState(false);
  const [designInProgress, setDesignInProgress] = useState(false);
  const [coverStatus, setCoverStatus] = useState(null); // Uses CoverStatus values
  const [coverReviewNotes, setCoverReviewNotes] = useState(null);
  const [authorFeedback, setAuthorFeedback] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackText, setFeedbackText] = useState(''); // For author to write feedback
  const [forwardNotes, setForwardNotes] = useState(''); // For author to add notes when forwarding rejection

  // Cover-only (standalone, not linked to a manuscript)
  const [isCoverOnly, setIsCoverOnly] = useState(false);
  
  // Designer permission state
  const [isAuthor, setIsAuthor] = useState(false);
  const [isDesigner, setIsDesigner] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [isCollaborator, setIsCollaborator] = useState(false);
  const [permissionLoaded, setPermissionLoaded] = useState(false);

  // Cover versions state
  const [versions, setVersions] = useState([]);
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [primaryVersionId, setPrimaryVersionId] = useState(null);
  // Start as true — versions are always fetched on load, so keep UI hidden until they arrive
  const [versionsLoading, setVersionsLoading] = useState(true);

  // Cover assignment state (hardcover/ebook)
  const [coverAssignments, setCoverAssignments] = useState({ hardcover: null, ebook: null });

  // New version dialog state
  const [newVersionDialogOpen, setNewVersionDialogOpen] = useState(false);

  // Create-options chooser (Create New / Copy from Existing / Use Template)
  const [createOptionsOpen, setCreateOptionsOpen] = useState(false);
  const [createFromExistingOpen, setCreateFromExistingOpen] = useState(false);

  // Cover templates state
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [creatingFromTemplate, setCreatingFromTemplate] = useState(false);

  // Cover market publish state (cover_only drafts)
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishForm, setPublishForm] = useState({ title: '', description: '', price: '9.99', tags: '', exclusive: false });
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishedCoverId, setPublishedCoverId] = useState(null);
  const [draftTitle, setDraftTitle] = useState('');

  // Collaborator state for active version
  const [activeVersionCollaborators, setActiveVersionCollaborators] = useState([]);

  // Send to Author (cover offer) state
  const [sendToAuthorOpen, setSendToAuthorOpen] = useState(false);
  const [authorSearch, setAuthorSearch] = useState('');
  const [authorSearchResults, setAuthorSearchResults] = useState([]);
  const [authorSearching, setAuthorSearching] = useState(false);
  const [sendingOffer, setSendingOffer] = useState(false);
  const [offerMessage, setOfferMessage] = useState('');
  const [offerPrice, setOfferPrice] = useState('');

  // View Details / Add Collaborators (per-version, opened from version menu)
  const [viewDetailsVersionId, setViewDetailsVersionId] = useState(null);
  const [addCollabVersionId, setAddCollabVersionId] = useState(null);
  const [addCollabSignal, setAddCollabSignal] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const t = localStorage.getItem('rv_access_token');
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.id || user._id;
        
        const res = await fetch(`${API_BASE}/api/drafts/${id}`, {
          headers: t ? { Authorization: `Bearer ${t}` } : {}
        });
        if (!res.ok) return;
        const d = await res.json();
        
        // Check permissions
        const authorMatch = String(d.author_id || d.author) === String(userId);
        const designerMatch = (d.designers || []).some(did => String(did) === String(userId));
        const collaboratorMatch = (d.collaborators || []).some(cid => String(cid) === String(userId));
        
        setIsAuthor(authorMatch);
        setIsDesigner(designerMatch);
        setIsCollaborator(collaboratorMatch);
        setHasPermission(authorMatch || designerMatch || collaboratorMatch);
        setPermissionLoaded(true);
        setIsCoverOnly(d.type === 'cover_only');
        const title = d.title || d.bookTitle || '';
        setDraftTitle(title);
        setPublishForm(prev => ({ ...prev, title }));
        
        // Note: hasDesign, designInProgress, coverStatus are set by fetchVersions
        // which runs right after permissionLoaded becomes true. We do NOT read
        // draft-level coverImage/coverDesign here because version-aware saves
        // only update per-version fields, not draft-level fields.
      } catch (e) {
        console.error('[BookCover] Failed to load draft:', e);
      }
    })();
  }, [id]);

  // Fetch cover versions
  const fetchVersions = useCallback(async () => {
    try {
      setVersionsLoading(true);
      const t = localStorage.getItem('rv_access_token');
      const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/versions?includeAllDesigns=true`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setVersions(data.versions || []);
        setActiveVersionId(data.activeVersionId);
        // The server resolves primary — including its fallback to the active
        // version when the author has never set one. Taking it straight (rather
        // than the old `prev ?? …`, which only ever seeded once) is what makes
        // the star survive a reload instead of drifting to the last version opened.
        setPrimaryVersionId(data.primaryVersionId ?? data.activeVersionId);
        // Set published cover ID from backend (for cover_only drafts)
        if (data.publishedCoverId) setPublishedCoverId(data.publishedCoverId);
        
        // Update current cover display based on active version
        const activeVersion = (data.versions || []).find(v => v.id === data.activeVersionId);
        if (activeVersion) {
          const anyDesign = activeVersion.hasDesign || activeVersion.hasWraparoundDesign;
          if (activeVersion.coverImage || anyDesign) {
            setHasDesign(true);
          } else {
            setHasDesign(false);
          }
          setCoverStatus(activeVersion.coverStatus || null);
          setCoverReviewNotes(activeVersion.coverReviewNotes || null);
          setAuthorFeedback(activeVersion.authorFeedback || null);
          setDesignInProgress(anyDesign && !activeVersion.coverImage);
          // Update collaborators for active version
          setActiveVersionCollaborators(activeVersion.collaborators || []);
        }
      }
    } catch (e) {
      console.error('[BookCover] Failed to fetch versions:', e);
    } finally {
      setVersionsLoading(false);
    }
  }, [id]);

  // Load versions after permission check
  useEffect(() => {
    if (permissionLoaded && !hasPermission) {
      // No permission — nothing to load, clear the loading guard
      setVersionsLoading(false);
    }
    if (hasPermission && permissionLoaded) {
      fetchVersions();
      // Fetch cover assignments for authors
      if (isAuthor) {
        fetchCoverAssignments();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission, permissionLoaded, fetchVersions, isAuthor, id]);

  // Fetch cover assignments (hardcover/ebook)
  const fetchCoverAssignments = async () => {
    try {
      const t = localStorage.getItem('rv_access_token');
      const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/assignments`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setCoverAssignments({ hardcover: data.hardcover, ebook: data.ebook });
      }
    } catch (e) {
      console.error('[BookCover] Failed to fetch cover assignments:', e);
    }
  };

  // Version management handlers
  const handleSelectVersion = async (versionId) => {
    if (versionId === activeVersionId) return;
    
    try {
      const t = localStorage.getItem('rv_access_token');
      const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/versions/${versionId}/activate`, {
        method: 'PUT',
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });
      
      if (res.ok) {
        setActiveVersionId(versionId);
        // Update display for selected version
        const selectedVersion = versions.find(v => v.id === versionId);
        if (selectedVersion) {
          const anyDesign = selectedVersion.hasDesign || selectedVersion.hasWraparoundDesign;
          if (selectedVersion.coverImage || anyDesign) {
            setHasDesign(true);
          } else {
            setHasDesign(false);
          }
          setCoverStatus(selectedVersion.coverStatus || null);
          setDesignInProgress(anyDesign && !selectedVersion.coverImage);
          setCoverReviewNotes(null);
          setAuthorFeedback(null);
          // Update collaborators for this version
          setActiveVersionCollaborators(selectedVersion.collaborators || []);
        }
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to switch version');
      }
    } catch (e) {
      console.error('Failed to switch version:', e);
      alert('Failed to switch version');
    }
  };

  // Setting a primary also switches to it, which is what the menu item has
  // always appeared to do. The difference is that the choice is now persisted
  // as its own thing, so it no longer evaporates into "last version opened".
  const handleSetPrimary = async (versionId) => {
    await handleSelectVersion(versionId);

    const previous = primaryVersionId;
    setPrimaryVersionId(versionId); // optimistic — the star should not lag the click
    try {
      const t = localStorage.getItem('rv_access_token');
      const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/versions/${versionId}/primary`, {
        method: 'PUT',
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setPrimaryVersionId(previous);
        alert(err.error || 'Failed to set the primary cover');
      }
    } catch (e) {
      console.error('[BookCover] Failed to set primary version:', e);
      setPrimaryVersionId(previous);
      alert('Failed to set the primary cover');
    }
  };

  // Plus sign now opens the creation-options chooser (author only)
  const handleOpenNewVersionDialog = () => {
    setCreateOptionsOpen(true);
  };

  // Handle applying a template — creates a new version with the template design pre-loaded
  const handleSelectTemplate = async (templateId) => {
    setCreatingFromTemplate(true);
    try {
      const t = localStorage.getItem('rv_access_token');
      const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/versions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {})
        },
        body: JSON.stringify({ templateId })
      });
      if (res.ok) {
        const data = await res.json();
        setVersions(prev => [...prev, data.version]);
        setActiveVersionId(data.activeVersionId);
        setActiveVersionCollaborators(data.version.collaborators || []);
        setHasDesign(data.version.hasWraparoundDesign || false);
        setCoverStatus(null);
        setDesignInProgress(data.version.hasWraparoundDesign || false);
        setTemplatesOpen(false);
        // Navigate straight to the designer so they can edit the template
        navigate(`/bookcover/${id}/designer/${data.activeVersionId}`);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to apply template');
      }
    } catch {
      alert('Failed to apply template');
    } finally {
      setCreatingFromTemplate(false);
    }
  };

  // Publish a reviewer-approved cover_only draft to the cover marketplace
  const handlePublishToMarket = async () => {
    setIsPublishing(true);
    try {
      const t = localStorage.getItem('rv_access_token');
      const tagsArray = publishForm.tags.split(',').map(s => s.trim()).filter(Boolean);
      const priceCents = Math.round(parseFloat(publishForm.price || '9.99') * 100);
      const res = await fetch(`${API_BASE}/api/cover-market/publish/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {})
        },
        body: JSON.stringify({
          title: publishForm.title || draftTitle || 'Untitled Cover',
          description: publishForm.description,
          price_cents: priceCents,
          tags: tagsArray,
          exclusive: publishForm.exclusive,
        })
      });
      if (res.ok) {
        const data = await res.json();
        setPublishedCoverId(data._id || data.id);
        setPublishDialogOpen(false);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to publish cover');
      }
    } catch {
      alert('Failed to publish cover');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCreateVersion = async (versionData) => {
    const t = localStorage.getItem('rv_access_token');

    // Guard the request itself: if the server crashes/hangs on a large source
    // design (or the network drops), fetch rejects here. Without this the error
    // would bubble past the dialog's silent catch and look like "nothing happened".
    let res;
    try {
      res = await fetch(`${API_BASE}/api/drafts/${id}/cover/versions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {})
        },
        body: JSON.stringify(versionData)
      });
    } catch (e) {
      console.error('Failed to create version (request failed):', e);
      alert('Could not reach the server to create the version. Please check your connection and try again.');
      throw e;
    }

    if (res.ok) {
      const data = await res.json();
      setVersions(prev => [...prev, data.version]);
      setActiveVersionId(data.activeVersionId);
      setActiveVersionCollaborators(data.version.collaborators || []);
      // A new version may be blank OR copied from an existing/template/purchased source.
      const copiedHasDesign = !!(data.version.hasDesign || data.version.hasWraparoundDesign);
      setHasDesign(copiedHasDesign);
      setCoverStatus(null);
      setDesignInProgress(copiedHasDesign);
    } else {
      const err = await res.json().catch(() => ({}));
      alert(err.error || 'Failed to create version');
      throw new Error(err.error || 'Failed to create version');
    }
  };

  // Get next version number for naming
  const getNextVersionNumber = () => {
    let max = 0;
    for (const v of versions) {
      const match = v.name?.match(/^Version (\d+)$/);
      if (match) max = Math.max(max, parseInt(match[1]));
    }
    return max + 1;
  };

  const handleRenameVersion = async (versionId, newName) => {
    try {
      const t = localStorage.getItem('rv_access_token');
      const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/versions/${versionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {})
        },
        body: JSON.stringify({ name: newName })
      });
      
      if (res.ok) {
        setVersions(prev => prev.map(v => 
          v.id === versionId ? { ...v, name: newName } : v
        ));
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to rename version');
      }
    } catch (e) {
      console.error('Failed to rename version:', e);
      alert('Failed to rename version');
    }
  };

  const handleDeleteVersion = async (versionId) => {
    try {
      const t = localStorage.getItem('rv_access_token');
      const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/versions/${versionId}`, {
        method: 'DELETE',
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });
      
      if (res.ok) {
        const data = await res.json();
        setVersions(prev => prev.filter(v => v.id !== versionId));
        
        // If deleted version was active, switch to new active
        if (versionId === activeVersionId && data.activeVersionId) {
          setActiveVersionId(data.activeVersionId);
          const newActive = versions.find(v => v.id === data.activeVersionId);
          if (newActive) {
            const anyDesign = newActive.hasDesign || newActive.hasWraparoundDesign;
            if (newActive.coverImage || anyDesign) {
              setHasDesign(true);
            } else {
              setHasDesign(false);
            }
            setCoverStatus(newActive.coverStatus || null);
            setDesignInProgress(anyDesign && !newActive.coverImage);
          }
        }
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to delete version');
      }
    } catch (e) {
      console.error('Failed to delete version:', e);
      alert('Failed to delete version');
    }
  };

  const handleDuplicateVersion = async (versionId) => {
    try {
      const t = localStorage.getItem('rv_access_token');
      const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/versions/${versionId}/duplicate`, {
        method: 'POST',
        headers: t ? { Authorization: `Bearer ${t}` } : {}
      });
      
      if (res.ok) {
        const data = await res.json();
        setVersions(prev => [...prev, data.version]);
        setActiveVersionId(data.activeVersionId);
        // Update display for duplicated version (has design but no image)
        setHasDesign(data.version.hasDesign || data.version.hasWraparoundDesign || false);
        setCoverStatus(null);
        setDesignInProgress((data.version.hasDesign || data.version.hasWraparoundDesign) && !data.version.coverImage);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to duplicate version');
      }
    } catch (e) {
      console.error('Failed to duplicate version:', e);
      alert('Failed to duplicate version');
    }
  };

  // Designer sends a specific version to author for approval
  const handleSendVersionToAuthor = async (versionId) => {
    if (!window.confirm('Send this version to the author for approval?')) return;
    
    try {
      const t = localStorage.getItem('rv_access_token');
      const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/versions/${versionId}/send-to-author`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {})
        }
      });
      
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        // Update the version's status in local state
        setVersions(prev => prev.map(v => 
          v.id === versionId ? { ...v, coverStatus: data.coverStatus || CoverStatus.WAITING_FOR_APPROVAL } : v
        ));
        // If this was the active version, update cover status
        if (versionId === activeVersionId) {
          setCoverStatus(data.coverStatus || CoverStatus.WAITING_FOR_APPROVAL);
        }
        alert('Version sent to author for approval!');
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to send version to author');
      }
    } catch (e) {
      console.error('Failed to send version to author:', e);
      alert('Failed to send version to author');
    }
  };

const handleDeleteCover = async () => {
  if (!window.confirm('Delete this cover and reset to defaults?')) return;
  try {
    const t = localStorage.getItem('rv_access_token');
    const res = await fetch(`${API_BASE}/api/drafts/${id}/cover`, {
      method: 'DELETE',
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    if (!res.ok) throw new Error(await res.text().catch(() => 'Failed'));

    // Reset local UI
    setHasDesign(false);
    alert('Cover deleted.');
  } catch (e) {
    console.error('Delete cover failed', e);
    alert('Failed to delete cover.');
  }
};

const handleDiscardChanges = async () => {
  if (!window.confirm('Discard your design progress? This cannot be undone.')) return;
  try {
    const t = localStorage.getItem('rv_access_token');
    const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/design`, {
      method: 'PUT',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(t ? { Authorization: `Bearer ${t}` } : {})
      },
      body: JSON.stringify({ design: null })
    });
    
    if (!res.ok) throw new Error(await res.text().catch(() => 'Failed'));

    // Refetch draft data to confirm it's cleared
    const refetchRes = await fetch(`${API_BASE}/api/drafts/${id}`, {
      headers: t ? { Authorization: `Bearer ${t}` } : {}
    });
    
    if (refetchRes.ok) {
      const d = await refetchRes.json();
      const hasProgressOnly = (!!d.coverDesign || !!d.wraparoundDesign) && !d.coverImage;
      setDesignInProgress(hasProgressOnly);
    } else {
      // Fallback: just reset local state
      setDesignInProgress(false);
    }
    
    alert('Design progress discarded.');
  } catch (e) {
    console.error('Discard changes failed', e);
    alert('Failed to discard changes.');
  }
};

const handleSubmitForReview = async () => {
  if (!hasDesign) return alert('Please save a cover first.');
  
  if (!window.confirm('Submit this cover for reviewer approval?')) return;
  
  setIsSubmitting(true);
  try {
    const t = localStorage.getItem('rv_access_token');
    const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/submit-review`, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(t ? { Authorization: `Bearer ${t}` } : {})
      }
    });
    
    if (!res.ok) {
      const msg = await res.json().catch(() => ({ error: 'Failed' }));
      throw new Error(msg.error || 'Failed to submit');
    }
    
    const data = await res.json().catch(() => ({}));
    setCoverStatus(data.coverStatus || CoverStatus.IN_REVIEW);
    alert('Cover submitted for review!');
  } catch (e) {
    console.error('Submit for review failed', e);
    alert(e.message || 'Failed to submit cover for review.');
  } finally {
    setIsSubmitting(false);
  }
};

// Designer sends cover to author for approval
const handleSendToAuthor = async () => {
  if (!hasDesign) return alert('Please save a cover first.');
  
  if (!window.confirm('Send this cover to the author for approval?')) return;
  
  setIsSubmitting(true);
  try {
    const t = localStorage.getItem('rv_access_token');
    const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/send-to-author`, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(t ? { Authorization: `Bearer ${t}` } : {})
      }
    });
    
    if (!res.ok) {
      const msg = await res.json().catch(() => ({ error: 'Failed' }));
      throw new Error(msg.error || 'Failed to send to author');
    }
    
    const data = await res.json().catch(() => ({}));
    setCoverStatus(data.coverStatus || CoverStatus.WAITING_FOR_APPROVAL);
    alert('Cover sent to author for approval!');
  } catch (e) {
    console.error('Send to author failed', e);
    alert(e.message || 'Failed to send cover to author.');
  } finally {
    setIsSubmitting(false);
  }
};

// Author approves designer's cover
const handleAuthorApprove = async () => {
  if (!window.confirm('Approve this cover? You can then submit it for reviewer approval.')) return;
  
  setIsSubmitting(true);
  try {
    const t = localStorage.getItem('rv_access_token');
    const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/author-approve`, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(t ? { Authorization: `Bearer ${t}` } : {})
      }
    });
    
    if (!res.ok) {
      const msg = await res.json().catch(() => ({ error: 'Failed' }));
      throw new Error(msg.error || 'Failed to approve');
    }
    
    const data = await res.json().catch(() => ({}));
    setCoverStatus(data.coverStatus || CoverStatus.APPROVED);
    alert('Cover approved! You can now submit it for review.');
  } catch (e) {
    console.error('Author approve failed', e);
    alert(e.message || 'Failed to approve cover.');
  } finally {
    setIsSubmitting(false);
  }
};

// Author requests changes from designer
const handleAuthorRequestChanges = async () => {
  if (!feedbackText.trim()) {
    return alert('Please provide feedback for the designer.');
  }
  
  if (!window.confirm('Send this feedback to the designer?')) return;
  
  setIsSubmitting(true);
  try {
    const t = localStorage.getItem('rv_access_token');
    const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/author-request-changes`, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(t ? { Authorization: `Bearer ${t}` } : {})
      },
      body: JSON.stringify({ feedback: feedbackText })
    });
    
    if (!res.ok) {
      const msg = await res.json().catch(() => ({ error: 'Failed' }));
      throw new Error(msg.error || 'Failed to request changes');
    }
    
    const data = await res.json().catch(() => ({}));
    setCoverStatus(data.coverStatus || CoverStatus.IN_PROGRESS);
    setAuthorFeedback(feedbackText);
    setFeedbackText('');
    alert('Feedback sent to designer!');
  } catch (e) {
    console.error('Request changes failed', e);
    alert(e.message || 'Failed to request changes.');
  } finally {
    setIsSubmitting(false);
  }
};

// Author forwards rejected cover to designer
const handleForwardToDesigner = async () => {
  if (!window.confirm('Forward this rejected cover to the designer for revision?')) return;
  
  setIsSubmitting(true);
  try {
    const t = localStorage.getItem('rv_access_token');
    const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/forward-to-designer`, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(t ? { Authorization: `Bearer ${t}` } : {})
      },
      body: JSON.stringify({ notes: forwardNotes })
    });
    
    if (!res.ok) {
      const msg = await res.json().catch(() => ({ error: 'Failed' }));
      throw new Error(msg.error || 'Failed to forward to designer');
    }
    
    const data = await res.json().catch(() => ({}));
    setCoverStatus(data.coverStatus || CoverStatus.IN_PROGRESS);
    setForwardNotes('');
    alert('Cover forwarded to designer!');
  } catch (e) {
    console.error('Forward to designer failed', e);
    alert(e.message || 'Failed to forward to designer.');
  } finally {
    setIsSubmitting(false);
  }
};

// Assign cover as hardcover/ebook (author only)
const handleAssignCover = async (versionId, coverType) => {
  const typeLabel = coverType === 'both' ? 'hardcover and ebook' : coverType;
  if (!window.confirm(`Assign this cover as ${typeLabel}?`)) return;
  
  setIsSubmitting(true);
  try {
    const t = localStorage.getItem('rv_access_token');
    const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/assign`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(t ? { Authorization: `Bearer ${t}` } : {})
      },
      body: JSON.stringify({ versionId, coverType })
    });
    
    if (!res.ok) {
      const msg = await res.json().catch(() => ({ error: 'Failed' }));
      throw new Error(msg.error || 'Failed to assign cover');
    }
    
    await fetchCoverAssignments();
    setCoverStatus(CoverStatus.ASSIGNED);
    alert(`Cover assigned as ${typeLabel}!`);
  } catch (e) {
    console.error('Assign cover failed', e);
    alert(e.message || 'Failed to assign cover.');
  } finally {
    setIsSubmitting(false);
  }
};

// Reset a cover version for re-editing (author only)
const handleResetVersion = async (versionId) => {
  if (!window.confirm('Reset this cover for re-editing? This will remove its approved status and any book assignments.')) return;
  
  setIsSubmitting(true);
  try {
    const t = localStorage.getItem('rv_access_token');
    const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/versions/${versionId}/reset`, {
      method: 'POST',
      headers: t ? { Authorization: `Bearer ${t}` } : {}
    });
    
    if (!res.ok) {
      const msg = await res.json().catch(() => ({ error: 'Failed' }));
      throw new Error(msg.error || 'Failed to reset cover');
    }
    
    // Update local state
    setVersions(prev => prev.map(v => 
      v.id === versionId ? { ...v, coverStatus: null } : v
    ));
    if (versionId === activeVersionId) {
      setCoverStatus(null);
    }
    await fetchCoverAssignments();
    alert('Cover reset for editing!');
  } catch (e) {
    console.error('Reset cover failed', e);
    alert(e.message || 'Failed to reset cover.');
  } finally {
    setIsSubmitting(false);
  }
};

// Reset a purchased version back to the original bought design
const handleResetToOriginalPurchase = async (versionId) => {
  if (!window.confirm('Reset this cover to the original purchased design? All your customizations will be lost.')) return;
  
  setIsSubmitting(true);
  try {
    const t = localStorage.getItem('rv_access_token');
    const res = await fetch(`${API_BASE}/api/drafts/${id}/cover/versions/${versionId}/reset-to-original`, {
      method: 'POST',
      headers: t ? { Authorization: `Bearer ${t}` } : {}
    });
    
    if (!res.ok) {
      const msg = await res.json().catch(() => ({ error: 'Failed' }));
      throw new Error(msg.error || 'Failed to reset to original');
    }
    
    // Refresh versions to get updated state
    await fetchVersions();
    alert('Cover restored to original purchased design!');
  } catch (e) {
    console.error('Reset to original failed', e);
    alert(e.message || 'Failed to reset to original.');
  } finally {
    setIsSubmitting(false);
  }
};

// Handle sending cover offer to an author
const handleSendCoverOffer = async (toUserId) => {
  setSendingOffer(true);
  try {
    const t = localStorage.getItem('rv_access_token');
    const res = await fetch(`${API_BASE}/api/invitations`, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        ...(t ? { Authorization: `Bearer ${t}` } : {}),
      },
      body: JSON.stringify({
        type: 'cover_offer',
        draftId: id,
        toUserId,
        message: offerMessage.trim() || undefined,
        price: offerPrice ? parseFloat(offerPrice) : undefined,
      }),
    });
    if (!res.ok) {
      const msg = await res.json().catch(() => ({ error: 'Failed' }));
      throw new Error(msg.error || 'Failed to send cover offer');
    }
    alert('Cover offer sent!');
    setSendToAuthorOpen(false);
    setAuthorSearch('');
    setAuthorSearchResults([]);
    setOfferMessage('');
    setOfferPrice('');
  } catch (e) {
    console.error('Send cover offer failed', e);
    alert(e.message || 'Failed to send cover offer.');
  } finally {
    setSendingOffer(false);
  }
};

// Search authors for cover offer
const handleAuthorSearch = async (query) => {
  setAuthorSearch(query);
  if (!query || query.length < 2) {
    setAuthorSearchResults([]);
    return;
  }
  setAuthorSearching(true);
  try {
    const t = localStorage.getItem('rv_access_token');
    const res = await fetch(`${API_BASE}/api/users/search?q=${encodeURIComponent(query)}`, {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    if (res.ok) {
      const data = await res.json();
      setAuthorSearchResults(data.users || data || []);
    }
  } catch { } finally {
    setAuthorSearching(false);
  }
};

// Helper to render status badge
const renderStatusBadge = () => {
  if (!coverStatus) return null;
  
  const statusConfig = {
    [CoverStatus.IN_PROGRESS]: { label: 'In Progress', color: '#6b7280', bg: '#f3f4f6' },
    [CoverStatus.WAITING_FOR_APPROVAL]: { label: 'Waiting for Approval', color: '#3b82f6', bg: '#dbeafe' },
    [CoverStatus.APPROVED]: { label: 'Approved by Author', color: '#8b5cf6', bg: '#ede9fe' },
    [CoverStatus.IN_REVIEW]: { label: 'In Review', color: '#f59e0b', bg: '#fef3c7' },
    [CoverStatus.REJECTED]: { label: 'Rejected', color: '#ef4444', bg: '#fee2e2' },
    [CoverStatus.REVIEWER_APPROVED]: { label: 'Reviewer Approved', color: '#10b981', bg: '#d1fae5' },
    [CoverStatus.ASSIGNED]: { label: 'Assigned to Book', color: '#059669', bg: '#d1fae5' },
  };
  
  const config = statusConfig[coverStatus];
  if (!config) return null;
  
  return (
    <div style={{
      display: 'inline-block',
      padding: '0.5rem 1rem',
      borderRadius: '9999px',
      backgroundColor: config.bg,
      color: config.color,
      fontWeight: 600,
      fontSize: '0.875rem',
      marginBottom: '1rem',
    }}>
      {config.label}
    </div>
  );
};

// Check if editing is allowed (permission + status)
// Designers can edit when in_progress (or null)
// Authors can edit when in_progress, rejected, or approved (before submitting for review)
const canDesignerEdit = isDesigner && (coverStatus === null || coverStatus === CoverStatus.IN_PROGRESS);
const canAuthorEdit = isAuthor && (
  coverStatus === null || 
  coverStatus === CoverStatus.IN_PROGRESS || 
  coverStatus === CoverStatus.REJECTED ||
  coverStatus === CoverStatus.APPROVED  // Author can still edit before sending for review
);
const canEdit = hasPermission && (canDesignerEdit || canAuthorEdit);

// Status flags for conditional rendering
const isInProgress = coverStatus === null || coverStatus === CoverStatus.IN_PROGRESS;
const isWaitingForApproval = coverStatus === CoverStatus.WAITING_FOR_APPROVAL;
const isApprovedByAuthor = coverStatus === CoverStatus.APPROVED;
const isInReview = coverStatus === CoverStatus.IN_REVIEW;
const isRejected = coverStatus === CoverStatus.REJECTED;
const isReviewerApproved = coverStatus === CoverStatus.REVIEWER_APPROVED;
const isAssigned = coverStatus === CoverStatus.ASSIGNED;

// Check if user has draft access (for back button)
// Cover-only drafts have no manuscript editor, so Back button goes to EditorHome instead
const hasDraftAccess = isAuthor || isCollaborator;

  // Show loading while checking permissions
  if (!permissionLoaded) {
    return (
      <div className="cover-page">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="cover-page">
      <h2>
        {designInProgress 
          ? 'Resume Your Book Cover Design' 
          : hasDesign 
            ? 'Your Book Cover' 
            : 'Select a Book Cover'}
      </h2>
      
      {/* Version Selector - Show for users with permission */}
      {hasPermission && (
        <VersionSelector
          versions={versions}
          activeVersionId={activeVersionId}
          onSelectVersion={handleSelectVersion}
          onCreateVersion={handleOpenNewVersionDialog}
          onRenameVersion={handleRenameVersion}
          onDeleteVersion={handleDeleteVersion}
          onDuplicateVersion={handleDuplicateVersion}
          onSendToAuthor={handleSendVersionToAuthor}
          primaryVersionId={primaryVersionId}
          onSetPrimary={handleSetPrimary}
          onViewDetails={(vid) => setViewDetailsVersionId(vid)}
          onAddCollaborators={(vid) => {
            setAddCollabVersionId(vid);
            setAddCollabSignal(s => s + 1);
          }}
          onViewTemplates={() => setTemplatesOpen(true)}
          disabled={isSubmitting || isInReview || isReviewerApproved || isAssigned}
          canEdit={canEdit}
          loading={versionsLoading}
          isAuthor={isAuthor}
          isDesigner={isDesigner}
        />
      )}

      {/* Collaborator Bar - Per-version collaborator management */}
      {/* New Version Dialog */}
      <NewVersionDialog
        open={newVersionDialogOpen}
        onClose={() => setNewVersionDialogOpen(false)}
        onConfirm={handleCreateVersion}
        nextVersionNumber={getNextVersionNumber()}
        purchasedVersions={versions.filter(v => v.isPurchased)}
      />

      {/* Cover Templates Modal - browse and apply design templates */}
      <CoverTemplatesModal
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        onSelectTemplate={handleSelectTemplate}
        isCreatingVersion={creatingFromTemplate}
      />

      {/* Create-options chooser — appears when author clicks the plus sign */}
      {createOptionsOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1300,
            backgroundColor: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem',
          }}
          onClick={() => setCreateOptionsOpen(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '440px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{ backgroundColor: '#413B8D', padding: '1.25rem 1.5rem' }}>
              <strong style={{ color: 'white', fontSize: '1.1rem' }}>How would you like to create this cover?</strong>
            </div>
            <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {[
                {
                  key: 'new',
                  title: 'Create New',
                  subtitle: 'Start from a blank version.',
                  onClick: () => { setCreateOptionsOpen(false); setNewVersionDialogOpen(true); },
                },
                {
                  key: 'copy',
                  title: 'Copy from Existing',
                  subtitle: 'Duplicate one of your existing cover versions.',
                  disabled: versions.length === 0,
                  onClick: () => { setCreateOptionsOpen(false); setCreateFromExistingOpen(true); },
                },
                {
                  key: 'template',
                  title: 'Use Template',
                  subtitle: 'Browse cover templates and start from one.',
                  onClick: () => { setCreateOptionsOpen(false); setTemplatesOpen(true); },
                },
              ].map(opt => (
                <button
                  key={opt.key}
                  disabled={opt.disabled}
                  onClick={opt.onClick}
                  style={{
                    textAlign: 'left',
                    padding: '0.85rem 1rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: opt.disabled ? '#f9fafb' : 'white',
                    cursor: opt.disabled ? 'not-allowed' : 'pointer',
                    opacity: opt.disabled ? 0.6 : 1,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (!opt.disabled) e.currentTarget.style.backgroundColor = '#f0eeff'; }}
                  onMouseLeave={e => { if (!opt.disabled) e.currentTarget.style.backgroundColor = 'white'; }}
                >
                  <div style={{ fontWeight: 600, color: '#111827' }}>{opt.title}</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.2rem' }}>{opt.subtitle}</div>
                </button>
              ))}
            </div>
            <div style={{ padding: '0.5rem 1rem 1rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setCreateOptionsOpen(false)}
                style={{
                  background: 'transparent', border: 'none',
                  color: '#6b7280', cursor: 'pointer', fontSize: '0.9rem',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create-From-Existing Dialog — full form with Copy From + auto-populate */}
      <CreateFromExistingDialog
        open={createFromExistingOpen}
        onClose={() => setCreateFromExistingOpen(false)}
        onConfirm={handleCreateVersion}
        nextVersionNumber={getNextVersionNumber()}
        versions={versions}
      />

      {/* Send to Author Dialog */}
      {sendToAuthorOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1300,
          backgroundColor: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1rem',
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '480px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            {/* Header */}
            <div style={{ backgroundColor: '#7c3aed', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '1.3rem' }}>👤</span>
              <strong style={{ color: 'white', fontSize: '1.1rem' }}>Send Cover to Author</strong>
            </div>
            {/* Body */}
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.35rem' }}>Search Author *</label>
                <input
                  type="text"
                  value={authorSearch}
                  onChange={e => handleAuthorSearch(e.target.value)}
                  placeholder="Type username to search..."
                  style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
                {authorSearching && <p style={{ margin: '0.5rem 0 0', color: '#9ca3af', fontSize: '0.8rem' }}>Searching...</p>}
                {authorSearchResults.length > 0 && (
                  <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', marginTop: '0.5rem' }}>
                    {authorSearchResults.map(u => (
                      <div
                        key={u.id || u._id}
                        onClick={() => handleSendCoverOffer(u.id || u._id)}
                        style={{
                          padding: '0.6rem 0.75rem',
                          cursor: sendingOffer ? 'not-allowed' : 'pointer',
                          borderBottom: '1px solid #f3f4f6',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                        onMouseLeave={e => e.currentTarget.style.background = 'white'}
                      >
                        <span style={{ fontSize: '1.1rem' }}>👤</span>
                        <span style={{ fontWeight: 600 }}>{u.username || u.name}</span>
                        {u.role && <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>({u.role})</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.35rem' }}>Message (optional)</label>
                <textarea
                  value={offerMessage}
                  onChange={e => setOfferMessage(e.target.value)}
                  placeholder="Add a message for the author..."
                  rows={3}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.35rem' }}>Suggested Price (optional)</label>
                <input
                  type="number"
                  value={offerPrice}
                  onChange={e => setOfferPrice(e.target.value)}
                  placeholder="e.g. 15.00"
                  min="0"
                  step="0.01"
                  style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            {/* Footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb' }}>
              <button
                className="confirm-btn"
                style={{ backgroundColor: '#6b7280' }}
                onClick={() => {
                  setSendToAuthorOpen(false);
                  setAuthorSearch('');
                  setAuthorSearchResults([]);
                  setOfferMessage('');
                  setOfferPrice('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      {/* View Details popup (per-version: Cover Type, Print Specs, Design Criteria) */}
      <ViewDetailsDialog
        open={!!viewDetailsVersionId}
        onClose={() => setViewDetailsVersionId(null)}
        draftId={id}
        version={versions.find(v => v.id === viewDetailsVersionId) || null}
        isAuthor={isAuthor}
        onSaved={(updates) => {
          setVersions(prev => prev.map(v =>
            v.id === viewDetailsVersionId ? { ...v, ...updates } : v
          ));
        }}
      />

      {/* Headless CollaboratorBar — hosts the Add Collaborators dialog triggered from the version menu */}
      {addCollabVersionId && (
        <CollaboratorBar
          draftId={id}
          versionId={addCollabVersionId}
          collaborators={(versions.find(v => v.id === addCollabVersionId)?.collaborators) || []}
          isAuthor={isAuthor}
          onCollaboratorsChange={(newCollabs) => {
            setVersions(prev => prev.map(v =>
              v.id === addCollabVersionId ? { ...v, collaborators: newCollabs } : v
            ));
            if (addCollabVersionId === activeVersionId) {
              setActiveVersionCollaborators(newCollabs);
            }
          }}
          openAddDialogSignal={addCollabSignal}
          hideButton
          hideList
        />
      )}

      {/* No Permission Message */}
      {!hasPermission && (
        <div style={{
          padding: '1.5rem',
          backgroundColor: '#fee2e2',
          borderRadius: '8px',
          border: '1px solid #fecaca',
          textAlign: 'center',
          margin: '2rem 0',
        }}>
          <p style={{ margin: 0, color: '#dc2626', fontWeight: 600, fontSize: '1.1rem' }}>
            ⚠️ You don't have permission to access this cover design.
          </p>
          <p style={{ margin: '0.5rem 0 0', color: '#7f1d1d', fontSize: '0.9rem' }}>
            Only the author and assigned collaborators can view and edit this cover.
          </p>
          {hasDraftAccess && (
            <button 
              className="confirm-btn" 
              style={{ marginTop: '1rem', backgroundColor: '#6b7280' }}
              onClick={() => navigate(`/editor/${id}`)}
            >
              ← Back to Editor
            </button>
          )}
        </div>
      )}
      
      {/* Status Badge */}
      {hasDesign && hasPermission && renderStatusBadge()}
      
      {/* Reviewer Approved Cover - manuscript: assign as hardcover/ebook; cover_only: publish to market */}
      {hasPermission && isReviewerApproved && isAuthor && (
        <div style={{
          padding: '1.5rem',
          marginBottom: '1rem',
          backgroundColor: '#d1fae5',
          borderRadius: '8px',
          border: '1px solid #a7f3d0',
        }}>
          <strong style={{ color: '#047857', fontSize: '1.1rem' }}>✅ Cover Approved by Reviewer!</strong>

          {isCoverOnly ? (
            <>
              <p style={{ margin: '0.75rem 0', color: '#065f46' }}>
                This standalone cover has been approved. You can now list it in the Cover Market for authors to purchase.
              </p>
              {publishedCoverId ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#059669',
                  borderRadius: '6px',
                  color: 'white',
                  marginBottom: '0.75rem',
                }}>
                  <span style={{ fontSize: '1.1rem' }}>🛒</span>
                  <span style={{ fontWeight: 600 }}>Listed in Cover Market!</span>
                </div>
              ) : (
                <button
                  className="confirm-btn"
                  style={{ backgroundColor: '#413B8D', marginBottom: '0.75rem' }}
                  onClick={() => setPublishDialogOpen(true)}
                  disabled={isSubmitting}
                >
                  🛒 List in Cover Market
                </button>
              )}

              {/* Send to Author button */}
              <button
                className="confirm-btn"
                style={{ backgroundColor: '#7c3aed', marginBottom: '0.75rem' }}
                onClick={() => setSendToAuthorOpen(true)}
                disabled={isSubmitting || sendingOffer}
              >
                👤 Send to Author
              </button>
            </>
          ) : (
            <>
              <p style={{ margin: '0.75rem 0', color: '#065f46' }}>
                This cover has been approved by the reviewer. You can now assign it to your book.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <button
                  className="confirm-btn"
                  style={{ backgroundColor: '#047857' }}
                  onClick={() => handleAssignCover(activeVersionId, 'hardcover')}
                  disabled={isSubmitting}
                >
                  📕 Set as Hardcover
                </button>
                <button
                  className="confirm-btn"
                  style={{ backgroundColor: '#0891b2' }}
                  onClick={() => handleAssignCover(activeVersionId, 'ebook')}
                  disabled={isSubmitting}
                >
                  📱 Set as Ebook Cover
                </button>
                <button
                  className="confirm-btn"
                  style={{ backgroundColor: '#7c3aed' }}
                  onClick={() => handleAssignCover(activeVersionId, 'both')}
                  disabled={isSubmitting}
                >
                  📚 Set as Both
                </button>
              </div>
            </>
          )}

          <button
            className="confirm-btn"
            style={{ backgroundColor: '#6b7280' }}
            onClick={() => handleResetVersion(activeVersionId)}
            disabled={isSubmitting}
          >
            🔄 Reset for Re-editing
          </button>

          {/* Reset to original purchased design - only for purchased versions */}
          {(() => {
            const activeVer = versions.find(v => v.id === activeVersionId);
            return activeVer?.isPurchased && activeVer?.hasOriginalPurchasedDesign;
          })() && (
            <button
              className="confirm-btn"
              style={{ backgroundColor: '#d97706', marginTop: '0.5rem' }}
              onClick={() => handleResetToOriginalPurchase(activeVersionId)}
              disabled={isSubmitting}
            >
              📦 Reset to Original Purchase
            </button>
          )}
        </div>
      )}

      {/* Publish to Cover Market dialog */}
      {publishDialogOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1300,
          backgroundColor: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1rem',
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '480px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            {/* Header */}
            <div style={{ backgroundColor: '#413B8D', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '1.3rem' }}>🛒</span>
              <strong style={{ color: 'white', fontSize: '1.1rem' }}>List in Cover Market</strong>
            </div>
            {/* Body */}
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.35rem' }}>Title *</label>
                <input
                  type="text"
                  value={publishForm.title}
                  onChange={e => setPublishForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="Cover title"
                  style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.35rem' }}>Description</label>
                <textarea
                  value={publishForm.description}
                  onChange={e => setPublishForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Short description of this cover design..."
                  rows={3}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.35rem' }}>Price (USD)</label>
                <input
                  type="number"
                  min="0.99"
                  step="0.01"
                  value={publishForm.price}
                  onChange={e => setPublishForm(p => ({ ...p, price: e.target.value }))}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.35rem' }}>Tags <span style={{ fontWeight: 400, color: '#9ca3af' }}>(comma-separated)</span></label>
                <input
                  type="text"
                  value={publishForm.tags}
                  onChange={e => setPublishForm(p => ({ ...p, tags: e.target.value }))}
                  placeholder="e.g. romance, minimalist, dark"
                  style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', fontSize: '0.9rem', color: '#374151' }}>
                <input
                  type="checkbox"
                  checked={publishForm.exclusive}
                  onChange={e => setPublishForm(p => ({ ...p, exclusive: e.target.checked }))}
                  style={{ width: 16, height: 16 }}
                />
                <span><strong>Exclusive</strong> — sell to one author only</span>
              </label>
            </div>
            {/* Footer */}
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                className="confirm-btn"
                style={{ backgroundColor: '#6b7280' }}
                onClick={() => setPublishDialogOpen(false)}
                disabled={isPublishing}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                style={{ backgroundColor: '#413B8D' }}
                onClick={handlePublishToMarket}
                disabled={isPublishing || !publishForm.title.trim()}
              >
                {isPublishing ? 'Publishing...' : '🛒 Publish Now'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Current Cover Assignments */}
      {hasPermission && isAuthor && (coverAssignments.hardcover || coverAssignments.ebook) && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          backgroundColor: '#f3f4f6',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}>
          <strong style={{ color: '#374151', fontSize: '1rem' }}>📖 Current Book Covers</strong>
          <div style={{ display: 'flex', gap: '2rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            {coverAssignments.hardcover && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  width: '80px', 
                  height: '120px', 
                  borderRadius: '4px',
                  overflow: 'hidden',
                  border: '2px solid #047857',
                  marginBottom: '0.5rem'
                }}>
                  {coverAssignments.hardcover.cover_url ? (
                    <img 
                      src={coverAssignments.hardcover.cover_url} 
                      alt="Hardcover"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>
                      No image
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#047857', fontWeight: 600 }}>Hardcover</div>
                <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>{coverAssignments.hardcover.versionName}</div>
              </div>
            )}
            {coverAssignments.ebook && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  width: '80px', 
                  height: '120px', 
                  borderRadius: '4px',
                  overflow: 'hidden',
                  border: '2px solid #0891b2',
                  marginBottom: '0.5rem'
                }}>
                  {coverAssignments.ebook.cover_url ? (
                    <img 
                      src={coverAssignments.ebook.cover_url} 
                      alt="Ebook"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>
                      No image
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#0891b2', fontWeight: 600 }}>Ebook</div>
                <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>{coverAssignments.ebook.versionName}</div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Rejection feedback - Author Triage Options */}
      {hasPermission && isRejected && isAuthor && (
        <div style={{
          padding: '1.5rem',
          marginBottom: '1rem',
          backgroundColor: '#fee2e2',
          borderRadius: '8px',
          border: '1px solid #fecaca',
        }}>
          <strong style={{ color: '#dc2626', fontSize: '1.1rem' }}>❌ Cover Rejected by Reviewer</strong>
          
          {/* Show reviewer notes if available */}
          {coverReviewNotes && (
            <div style={{ 
              padding: '0.75rem', 
              marginTop: '0.75rem',
              backgroundColor: 'white',
              borderRadius: '6px',
              border: '1px solid #fca5a5'
            }}>
              <p style={{ margin: 0, color: '#7f1d1d', whiteSpace: 'pre-wrap' }}>{coverReviewNotes}</p>
            </div>
          )}
          
          {!coverReviewNotes && (
            <p style={{ margin: '0.75rem 0 0', color: '#7f1d1d', fontStyle: 'italic' }}>
              No specific feedback was provided by the reviewer.
            </p>
          )}
          
          <p style={{ margin: '1rem 0 0.5rem', color: '#7f1d1d', fontWeight: 600 }}>
            Choose how to proceed:
          </p>
          
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <button
              className="confirm-btn"
              style={{ backgroundColor: '#1976d2' }}
              onClick={() => navigate(`/bookcover/${id}/designer${activeVersionId ? `/${activeVersionId}` : ''}`)}
            >
              ✏️ Edit Cover Myself
            </button>
            
            <button
              className="confirm-btn"
              style={{ backgroundColor: '#f97316' }}
              onClick={() => {
                const designCollaborators = (activeVersionCollaborators || []).filter(c => c.role === 'design');
                if (designCollaborators.length === 0) {
                  alert('Please add a collaborator with "Design" role first using the Collaborator Bar above.');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  document.getElementById('forward-section').style.display = 'block';
                }
              }}
            >
              📤 Forward to Designer
            </button>
          </div>
          
          {/* Forward to Designer Section - only show if design collaborators exist */}
          {(activeVersionCollaborators || []).some(c => c.role === 'design') && (
            <div id="forward-section" style={{ display: 'none', borderTop: '1px solid #fca5a5', paddingTop: '1rem' }}>
              <p style={{ margin: '0 0 0.5rem', color: '#7f1d1d', fontSize: '0.9rem' }}>
                Add notes for the designer (optional):
              </p>
              <textarea
                value={forwardNotes}
                onChange={(e) => setForwardNotes(e.target.value)}
                placeholder="Add any additional context or instructions..."
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  border: '1px solid #fca5a5',
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem',
                  resize: 'vertical',
                }}
              />
              <button
                className="confirm-btn"
                style={{ backgroundColor: '#f97316' }}
                onClick={handleForwardToDesigner}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Forwarding...' : 'Send to Designer'}
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Rejection feedback - Designer Waiting Message */}
      {hasPermission && isRejected && isDesigner && !isAuthor && (
        <div style={{
          padding: '1.5rem',
          marginBottom: '1rem',
          backgroundColor: '#fef3c7',
          borderRadius: '8px',
          border: '1px solid #fde68a',
          textAlign: 'center',
        }}>
          <p style={{ margin: 0, color: '#92400e', fontWeight: 600, fontSize: '1.1rem' }}>
            ⏳ Cover Rejected - Awaiting Author Decision
          </p>
          <p style={{ margin: '0.75rem 0 0', color: '#78350f' }}>
            This cover was rejected by the reviewer. The author will review the feedback and decide whether to fix it themselves or forward it to you for revision.
          </p>
        </div>
      )}
      
      {/* Author feedback to designer (shown when in_progress after rejection forward) */}
      {hasPermission && isDesigner && isInProgress && authorFeedback && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          backgroundColor: '#ffedd5',
          borderRadius: '8px',
          border: '1px solid #fed7aa',
        }}>
          <strong style={{ color: '#c2410c' }}>Author Feedback:</strong>
          <p style={{ margin: '0.5rem 0 0', color: '#9a3412', whiteSpace: 'pre-wrap' }}>{authorFeedback}</p>
        </div>
      )}
      
      {/* Waiting for author approval message - for designer */}
      {hasPermission && isDesigner && isWaitingForApproval && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          backgroundColor: '#dbeafe',
          borderRadius: '8px',
          border: '1px solid #bfdbfe',
          textAlign: 'center',
        }}>
          <p style={{ margin: 0, color: '#1e40af' }}>
            ⏳ Your cover is waiting for author approval. You'll be notified once it's approved or if changes are requested.
          </p>
        </div>
      )}
      
      {/* Waiting for author approval message - for author with action buttons */}
      {hasPermission && isAuthor && isWaitingForApproval && (
        <div style={{
          padding: '1.5rem',
          marginBottom: '1rem',
          backgroundColor: '#dbeafe',
          borderRadius: '8px',
          border: '1px solid #bfdbfe',
        }}>
          <p style={{ margin: 0, color: '#1e40af', fontWeight: 600, marginBottom: '1rem' }}>
            🎨 A designer has submitted this cover for your approval.
          </p>
          
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <button
              className="confirm-btn"
              style={{ backgroundColor: '#10b981' }}
              onClick={handleAuthorApprove}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : '✓ Approve Cover'}
            </button>
          </div>
          
          <div style={{ borderTop: '1px solid #bfdbfe', paddingTop: '1rem' }}>
            <p style={{ margin: '0 0 0.5rem', color: '#1e40af', fontSize: '0.9rem' }}>
              Or request changes:
            </p>
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Provide feedback for the designer..."
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '0.75rem',
                borderRadius: '6px',
                border: '1px solid #bfdbfe',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                resize: 'vertical',
              }}
            />
            <button
              className="confirm-btn"
              style={{ backgroundColor: '#f97316' }}
              onClick={handleAuthorRequestChanges}
              disabled={isSubmitting || !feedbackText.trim()}
            >
              {isSubmitting ? 'Sending...' : 'Request Changes'}
            </button>
          </div>
        </div>
      )}
      
      {/* In review message */}
      {hasPermission && isInReview && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          backgroundColor: '#fef3c7',
          borderRadius: '8px',
          border: '1px solid #fde68a',
          textAlign: 'center',
        }}>
          <p style={{ margin: 0, color: '#92400e' }}>
            Your cover is currently under review. You'll be notified once it's approved or if changes are needed.
          </p>
        </div>
      )}
      
      {/* Reviewer approved message */}
      {hasPermission && isReviewerApproved && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          backgroundColor: '#d1fae5',
          borderRadius: '8px',
          border: '1px solid #a7f3d0',
          textAlign: 'center',
        }}>
          <p style={{ margin: 0, color: '#065f46' }}>
            ✓ Your cover has been approved by a reviewer!
          </p>
        </div>
      )}

      {/* Assigned status - cover is assigned to the book */}
      {hasPermission && isAssigned && (
        <div style={{
          padding: '1.5rem',
          marginBottom: '1rem',
          backgroundColor: '#ede9fe',
          borderRadius: '8px',
          border: '1px solid #c4b5fd',
        }}>
          <strong style={{ color: '#5b21b6', fontSize: '1.1rem' }}>📖 Cover Assigned to Book</strong>
          <p style={{ margin: '0.75rem 0', color: '#6d28d9' }}>
            This cover has been assigned to your book.
            {coverAssignments.hardcover && coverAssignments.ebook
              ? ' It is set as both hardcover and ebook cover.'
              : coverAssignments.hardcover
                ? ' It is set as the hardcover.'
                : coverAssignments.ebook
                  ? ' It is set as the ebook cover.'
                  : ''}
          </p>
          {isAuthor && (
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                className="confirm-btn"
                style={{ backgroundColor: '#6b7280' }}
                onClick={() => handleResetVersion(activeVersionId)}
                disabled={isSubmitting}
              >
                🔄 Reset for Re-editing
              </button>
            </div>
          )}
        </div>
      )}

      {hasPermission && !versionsLoading && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center', marginTop: '1.5rem' }}>
          {/* Edit/Design button - only when editable (hide when rejected + author, since rejection triage has its own edit button) */}
          {canEdit && !(isRejected && isAuthor) && (
            <button
              className="confirm-btn"
              style={{ backgroundColor: designInProgress ? '#D4AF37' : '#1976d2' }}
              onClick={() => navigate(`/bookcover/${id}/designer${activeVersionId ? `/${activeVersionId}` : ''}`)}
            >
              {designInProgress 
                ? 'Resume Design' 
                : (isInProgress && authorFeedback)
                  ? 'Edit & Resubmit'
                  : hasDesign 
                    ? 'Edit Cover' 
                    : 'Design Your Own Cover'}
            </button>
          )}

          {designInProgress && canEdit && (
            <button
              className="confirm-btn"
              style={{ backgroundColor: '#ff6f00' }}
              onClick={handleDiscardChanges}
            >
              Discard Changes
            </button>
          )}

          {/* Send for Approval - Designer sends to author (not when rejected, approved, or in review) */}
          {isDesigner && hasDesign && !designInProgress && !isWaitingForApproval && !isInReview && !isReviewerApproved && !isAssigned && !isRejected && !isApprovedByAuthor && (
            <button
              className="confirm-btn"
              style={{ backgroundColor: '#3b82f6' }}
              onClick={handleSendToAuthor}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : (isInProgress && authorFeedback) ? 'Resend for Approval' : 'Send for Approval'}
            </button>
          )}

          {/* Submit for Review - only author can submit after approving designer work or creating own cover */}
          {hasDesign && !designInProgress && !isInReview && !isReviewerApproved && !isAssigned && !isWaitingForApproval && isAuthor && (isApprovedByAuthor || isInProgress) && (
            <button
              className="confirm-btn"
              style={{ backgroundColor: '#7c3aed' }}
              onClick={handleSubmitForReview}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit for Review'}
            </button>
          )}

          {/* View Feedback - Author can view feedback from collaborators */}
          {hasDesign && !designInProgress && isAuthor && (activeVersionCollaborators || []).length > 0 && activeVersionId && (
            <button
              className="confirm-btn"
              style={{ backgroundColor: '#0ea5e9' }}
              onClick={() => navigate(`/bookcover/${id}/designer/${activeVersionId}`)}
            >
              👁️ View Feedback
            </button>
          )}

          {/* Delete button - only when editable and has design */}
          {hasDesign && !designInProgress && canEdit && (
            <button
              className="confirm-btn"
              style={{ backgroundColor: '#b00020' }}
              onClick={handleDeleteCover}
            >
              Delete Cover
            </button>
          )}
          
          {/* Back navigation - cover-only goes to EditorHome, manuscript cover goes to editor */}
          {hasDraftAccess && (
            <button
              className="confirm-btn"
              style={{ backgroundColor: '#6b7280' }}
              onClick={() => isCoverOnly ? navigate('/editor') : navigate(`/editor/${id}`)}
            >
              {isCoverOnly ? '← My Covers' : '← Back to Editor'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
